import { useEffect, useState, useRef } from 'react';
import api, { API_BASE } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, Modal, Input, Select, Loading, Alert } from '../components/ui';
import Companies from '../components/Companies';
import LookupLists from '../components/LookupLists';

const empty = { name:'', code:'', address:'', phone:'', email:'', is_default: false, status:'active', notes:'' };

export default function Settings() {
  const { can } = useAuth();
  const [tab, setTab] = useState('branches');
  const [branches, setBranches] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(empty);
  const [saving,   setSaving]   = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadBranch, setUploadBranch] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [lhFile,   setLhFile]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(); const lhRef = useRef();

  const flash = (msg, isErr=false) => {
    isErr ? setError(msg) : setSuccess(msg);
    setTimeout(() => isErr ? setError('') : setSuccess(''), 3500);
  };

  const load = () => {
    setLoading(true);
    api.get('/branches')
      .then(r => setBranches(r.data.data || []))
      .catch(() => flash('Failed to load branches', true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = b  => { setEditing(b); setForm({...empty,...b}); setModal(true); };

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editing ? await api.put(`/branches/${editing.id}`, form) : await api.post('/branches', form);
      setModal(false); load();
      flash(editing ? 'Branch updated' : 'Branch added');
    } catch (err) { flash(err.response?.data?.message || 'Save failed', true); }
    finally { setSaving(false); }
  };

  const remove = async id => {
    if (!window.confirm('Delete this branch?')) return;
    try { await api.delete(`/branches/${id}`); load(); flash('Branch deleted'); }
    catch { flash('Delete failed', true); }
  };

  const setDefault = async b => {
    try {
      await api.put(`/branches/${b.id}`, { ...b, is_default: true });
      // Remove default from others
      for (const other of branches.filter(x => x.id !== b.id && x.is_default)) {
        await api.put(`/branches/${other.id}`, { ...other, is_default: false });
      }
      load(); flash(`${b.name} set as default branch`);
    } catch { flash('Failed', true); }
  };

  const openUpload = b => { setUploadBranch(b); setLogoFile(null); setLhFile(null); setUploadModal(true); };

  const handleUpload = async () => {
    if (!logoFile && !lhFile) return flash('Select at least one file', true);
    setUploading(true);
    const fd = new FormData();
    if (logoFile) fd.append('logo', logoFile);
    if (lhFile)   fd.append('letterhead', lhFile);
    try {
      await api.post(`/branches/${uploadBranch.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadModal(false); load();
      flash('Files uploaded successfully');
    } catch (err) { flash(err.response?.data?.message || 'Upload failed', true); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        sub={tab === 'branches' ? 'Branches and their letterheads for official letters'
          : tab === 'companies' ? 'Companies that own vehicles'
          : 'Manage the dropdown options used across the system'}
        action={tab === 'branches' && can('admin') && <Button onClick={openAdd}>+ Add Branch</Button>}
      />

      <div className="flex gap-2 mb-4">
        {[['branches','🏢 Branches & Letterheads'],['companies','🏛 Companies'],['lists','📋 Dropdown Lists']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === k ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'companies' ? <Companies /> : tab === 'lists' ? <LookupLists /> : (<>

      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
        <strong>How branches work:</strong> Each branch has its own name, address, and optional letterhead PDF.
        When generating quotation request letters or award letters in the Repairs section,
        you select which branch is issuing the letter — the branch details appear in the letter header.
        Set one branch as <strong>Default</strong> so it's pre-selected automatically.
      </div>

      {loading ? <Loading /> : (
        <div className="space-y-4">
          {branches.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <p className="text-4xl mb-2">🏢</p>
              <p className="text-gray-600 font-medium">No branches yet</p>
              <p className="text-gray-400 text-sm mb-4">Add your company branch to use on official letters</p>
              <Button onClick={openAdd}>+ Add First Branch</Button>
            </div>
          )}
          {branches.map(b => (
            <div key={b.id} className={`bg-white rounded-xl border-2 p-5
              ${b.is_default ? 'border-primary-400 bg-primary-50' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  {/* Logo preview */}
                  <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {b.logo_path
                      ? <img src={`${API_BASE}${b.logo_path}`} alt="logo" className="w-full h-full object-contain" />
                      : <span className="text-2xl">🏢</span>}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800">{b.name}</h3>
                      {b.code && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{b.code}</span>}
                      {b.is_default && <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">★ Default</span>}
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{b.status}</span>
                    </div>
                    <p className="text-sm text-gray-600">{b.address || '—'}</p>
                    <p className="text-sm text-gray-500">{b.phone} {b.email ? `· ${b.email}` : ''}</p>
                    <div className="flex gap-3 mt-2">
                      {b.letterhead_path
                        ? <a href={`${API_BASE}${b.letterhead_path}`} target="_blank" rel="noreferrer"
                            className="text-xs text-blue-600 underline flex items-center gap-1">
                            📄 View Letterhead
                          </a>
                        : <span className="text-xs text-gray-400">No letterhead uploaded</span>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(b)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => openUpload(b)}>📎 Upload</Button>
                  {!b.is_default && (
                    <Button variant="ghost" size="sm" onClick={() => setDefault(b)}>Set Default</Button>
                  )}
                  {can('admin') && (
                    <Button variant="danger" size="sm" onClick={() => remove(b.id)}>Delete</Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      </>)}

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? `Edit — ${editing.name}` : 'Add Branch / Institute'} size="md">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Branch Name *" value={form.name}
              onChange={e => setForm({...form, name: e.target.value})} required
              placeholder="e.g. HIT Head Office" />
            <Input label="Branch Code" value={form.code}
              onChange={e => setForm({...form, code: e.target.value})}
              placeholder="e.g. HIT-HO" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-primary-300 h-20 resize-none"
              placeholder="Full address that appears on letters" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Phone" value={form.phone}
              onChange={e => setForm({...form, phone: e.target.value})} placeholder="011 XXXXXXX" />
            <Input label="Email" type="email" value={form.email}
              onChange={e => setForm({...form, email: e.target.value})} placeholder="info@hit.lk" />
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="is_default" checked={form.is_default}
              onChange={e => setForm({...form, is_default: e.target.checked})}
              className="w-4 h-4 text-primary-600" />
            <label htmlFor="is_default" className="text-sm text-gray-700">
              Set as default branch (pre-selected in letters)
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Branch'}</Button>
          </div>
        </form>
      </Modal>

      {/* Upload Modal */}
      <Modal open={uploadModal} onClose={() => setUploadModal(false)}
        title={`Upload Files — ${uploadBranch?.name}`} size="sm">
        <div className="space-y-5">
          <div className="border-2 border-dashed border-blue-200 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">🖼 Branch Logo (PNG/JPG)</p>
            <input ref={logoRef} type="file" accept="image/*" className="hidden"
              onChange={e => setLogoFile(e.target.files[0])} />
            <Button variant="ghost" size="sm" onClick={() => logoRef.current.click()}>
              {logoFile ? `✓ ${logoFile.name}` : 'Select Logo'}
            </Button>
            <p className="text-xs text-gray-400 mt-1">Appears on letters and system UI</p>
          </div>
          <div className="border-2 border-dashed border-green-200 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">📄 Letterhead PDF</p>
            <input ref={lhRef} type="file" accept=".pdf" className="hidden"
              onChange={e => setLhFile(e.target.files[0])} />
            <Button variant="ghost" size="sm" onClick={() => lhRef.current.click()}>
              {lhFile ? `✓ ${lhFile.name}` : 'Select PDF'}
            </Button>
            <p className="text-xs text-gray-400 mt-1">Used as background on printed letters</p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setUploadModal(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || (!logoFile && !lhFile)}>
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
